/**
 * LyX (cua.bind + menus.bind + math.bind) keyboard bindings, including the Alt+P / Alt+A /
 * Alt+M / Alt+S prefix chords.
 */
import { keymap } from 'prosemirror-keymap';
import { Plugin, PluginKey, TextSelection, type Command, type EditorState } from 'prosemirror-state';
import { undoInputRule } from 'prosemirror-inputrules';
import { baseKeymap, chainCommands, deleteSelection, joinBackward, selectNodeBackward, joinForward, selectNodeForward, selectAll } from 'prosemirror-commands';
import { undo, redo } from './collab';
import { goToNextCell } from 'prosemirror-tables';
import type { EditorView } from 'prosemirror-view';
import {
  paragraphBreak, paragraphBreakInverse, fontCommands, fontDefault, changeDepth, listIndent, insertMath, toggleMathDisplay, typeDollar,
  insertNewline, insertSpace, insertSpecial, insertERT, insertFootnote, insertNote, insertComment, selectInset, toggleInset,
  moveParagraph, setLayout, setKnownLayout, setParagraphAttrs, arrowIntoMath, setValueMark, insertHyphens, insertDash, insertQuote, smartQuote, insertMarginal,
 listExitBackspace } from './commands';
import { editorContext } from './context';
import { activeMathField } from './lyxmath/field';
import { ATOMS } from './plugins/dragselect';
import { trackedDelete } from './plugins/changes';
import { openRewrite, rewriteEnabled } from './ai/rewrite';
import { openLinkBox } from './links';
import { recordUsage } from '../usage';
import { moveListItem } from './listmove';

export interface UiActions {
  save(): void;
  viewPdf(): void;
  updatePdf(): void;
  /** SyncTeX forward search: show the cursor's place in the PDF */
  syncToPdf?(): void;
  find(): void;
  openDialog(name: string, arg?: unknown): void;
  toggleTrackChanges(): void;
  toggleOutline(): void;
  zoom(delta: number): void;
  openFile(): void;
  newFile(): void;
  toggleCombined?(): void;
  acceptAll?(): void;
  rejectAll?(): void;
  closeTab?(): void;
  toggleSource?(): void;
  /** change the text column width by a step (0 resets) */
  textWidth?(delta: number): void;
}

const ui = (fn: (a: UiActions) => void): Command => () => { const a = editorContext.ui; if (a) fn(a); return true; };

/**
 * Usage statistics (usage.ts): the shortcuts worth counting — modifier combinations, F-keys, Tab and
 * Escape; not typing keys, arrows or the word deletes — and whether their command did anything.
 */
const TRACED_KEY = /^(?:(?:Shift-)?Tab|Escape|F\d+)$|^(?=.*(?:Mod|Alt|Ctrl)-)(?!.*(?:Arrow\w*|Home|End|Page\w*|Backspace|Delete)$)/;
const traced = (key: string, cmd: Command): Command => (state, dispatch, view) => {
  const ok = cmd(state, dispatch, view);
  if (dispatch) recordUsage('key', key, { ok });
  return ok;
};
/** 'M-p' → 'Alt+P' for the statistics */
const chordLabel = (prefix: string) => prefix.replace(/^M-(\w)/, (_m, c: string) => 'Alt+' + c.toUpperCase());

/** LyX word-delete-backward/forward: within a paragraph's text; at an inset or paragraph edge the character command takes over. */
const deleteWord = (dir: -1 | 1, fallback: Command): Command => (state, dispatch, view) => {
  const { $from, empty } = state.selection;
  if (!empty || !$from.parent.isTextblock) return fallback(state, dispatch, view);
  const off = $from.parentOffset;
  const par = $from.parent;
  if (dir < 0) {
    const before = par.textBetween(0, off, '\u0000', '\u0000');
    const m = /(\s*\S+|\s+)$/.exec(before);
    if (!m || m[0].includes('\u0000')) return fallback(state, dispatch, view);
    if (dispatch) dispatch(state.tr.delete($from.pos - m[0].length, $from.pos).scrollIntoView());
    return true;
  }
  const after = par.textBetween(off, par.content.size, '\u0000', '\u0000');
  const m = /^(\S+\s*|\s+)/.exec(after);
  if (!m || m[0].includes('\u0000')) return fallback(state, dispatch, view);
  if (dispatch) dispatch(state.tr.delete($from.pos, $from.pos + m[0].length).scrollIntoView());
  return true;
};

declare module './context' { interface EditorContext { ui?: UiActions } }

const LAYOUT_PREFIX: Record<string, string> = {
  '0': 'Part', '1': 'Chapter', '2': 'Section', '3': 'Subsection', '4': 'Subsubsection', '5': 'Paragraph', '6': 'Subparagraph',
  a: 'Abstract', A: 'Author', B: 'Bibliography', c: 'LyX-Code', C: 'Comment', d: 'Description', D: 'Date', e: 'Enumerate',
  i: 'Itemize', l: 'Labeling', n: 'Enumerate', q: 'Quote', Q: 'Quotation', s: 'Standard', t: 'Title', v: 'Verse', L: 'LaTeX',
};

const SIZE_PREFIX: Record<string, string> = { t: 'tiny', S: 'footnotesize', s: 'small', n: 'normal', l: 'large', L: 'larger', h: 'huge', H: 'giant', '1': 'tiny', '2': 'scriptsize', '3': 'footnotesize', '4': 'small', '5': 'normal', '6': 'large', '7': 'larger', '8': 'largest', '9': 'huge', '0': 'giant' };

export const chordKey = new PluginKey<string | null>('lyx-chord');

/** Prefix-chord plugin: Alt+P (layouts), Alt+A (paragraph), Alt+M (math), Alt+S (size). */
export function chordPlugin(): Plugin<string | null> {
  return new Plugin<string | null>({
    key: chordKey,
    state: {
      init: () => null,
      apply: (tr, prev) => (tr.getMeta(chordKey) !== undefined ? tr.getMeta(chordKey) : prev),
    },
    props: {
      handleKeyDown(view, ev) {
        const prefix = chordKey.getState(view.state);
        const setPrefix = (p: string | null) => view.dispatch(view.state.tr.setMeta(chordKey, p));
        if (prefix) {
          if (ev.key === 'Shift' || ev.key === 'Control' || ev.key === 'Alt' || ev.key === 'Meta') return false;
          setPrefix(null);
          if (ev.key === 'Escape') return true;
          const k = ev.key;
          recordUsage('chord', chordLabel(prefix));
          /** a key the chord does not know: LyX swallows it too, but it is what the person tried */
          const miss = () => { recordUsage('chord', `${chordLabel(prefix)} ${k}`, { ok: false }); return true; };
          if (prefix === 'M-p') {
            if (k === '*') { setPrefix('M-p*'); return true; }
            const layout = LAYOUT_PREFIX[k];
            if (layout) { setLayout(layout)(view.state, view.dispatch); return true; }
            if (k === ' ') { editorContext.ui?.openDialog('layout'); return true; }
            if (k === 'ArrowLeft') { changeDepth(-1)(view.state, view.dispatch); return true; }
            if (k === 'ArrowRight') { changeDepth(1)(view.state, view.dispatch); return true; }
            if (k === 'ArrowUp') { moveParagraph(-1)(view.state, view.dispatch); return true; }
            if (k === 'ArrowDown') { moveParagraph(1)(view.state, view.dispatch); return true; }
            if (k === 'Enter') { paragraphBreakInverse(view.state, view.dispatch); return true; }
            return miss();
          }
          if (prefix === 'M-p*') {
            const layout = LAYOUT_PREFIX[k];
            if (layout && /^[0-6]$/.test(k)) { setLayout(layout + '*')(view.state, view.dispatch); return true; }
            return miss();
          }
          if (prefix === 'M-a') {
            const map: Record<string, Command> = {
              l: setParagraphAttrs({ align: 'left' }), r: setParagraphAttrs({ align: 'right' }), c: setParagraphAttrs({ align: 'center' }),
              j: setParagraphAttrs({ align: 'block' }), e: setParagraphAttrs({ align: null }),
              i: (s, d) => { const cur = s.selection.$from; for (let dd = cur.depth; dd >= 0; dd--) if (cur.node(dd).type.name === 'paragraph') return setParagraphAttrs({ noindent: !cur.node(dd).attrs.noindent })(s, d); return false; },
              s: setParagraphAttrs({ spacing: 'single' }), o: setParagraphAttrs({ spacing: 'onehalf' }), d: setParagraphAttrs({ spacing: 'double' }), f: setParagraphAttrs({ spacing: null }),
            };
            if (map[k]) { map[k](view.state, view.dispatch); return true; }
            if (/^[0-9]$/.test(k)) { editorContext.ui?.openDialog('argument', k === '0' ? 'post:1' : k); return true; }
            return miss();
          }
          if (prefix === 'M-s') {
            const size = SIZE_PREFIX[k];
            if (size) { setValueMark('size', size === 'normal' ? null : size)(view.state, view.dispatch); return true; }
            return miss();
          }
          if (prefix === 'M-m') {
            if (k === 'm') { insertMath(false)(view); return true; }
            if (k === 'd') { insertMath(true)(view); return true; }
            if (k === 'n') { insertMath(true, 'equation')(view); return true; }
            if (k === 't') { setPrefix('M-m t'); return true; }
            if (k === 'f') { insertMath(false)(view); setTimeout(() => activeMathField()?.execute('insert', '\\frac{#0}{}'), 30); return true; }
            return miss();
          }
          if (prefix === 'M-m t') {
            const envs: Record<string, string> = { a: 'align', i: 'simple', d: 'equation', e: 'eqnarray', m: 'multline', g: 'gather', n: 'simple' };
            if (envs[k]) { insertMath(true, envs[k] === 'simple' ? undefined : envs[k])(view); return true; }
            return miss();
          }
          return true;
        }
        if (ev.altKey && !ev.ctrlKey && !ev.metaKey) {
          const k = ev.key.toLowerCase();
          if (k === 'p' || k === 'a' || k === 'm' || k === 's') { setPrefix('M-' + k); ev.preventDefault(); return true; }
        }
        // Escape leaves an inset (LyX: cancel)
        return false;
      },
    },
  });
}

// the marker typed for a markdown trigger comes back first (undoInputRule), then Backspace at the start of a list item leaves the list
const backspace: Command = chainCommands(undoInputRule, listExitBackspace, trackedDelete(-1), deleteSelection, joinBackward, selectNodeBackward);
const del: Command = chainCommands(trackedDelete(1), deleteSelection, joinForward, selectNodeForward);

export function lyxKeymap(): Plugin {
  const bindings: Record<string, Command> = {
    ...baseKeymap,
    Enter: paragraphBreak,
    'Alt-Enter': paragraphBreakInverse,
    'Mod-Enter': insertNewline('newline'),
    'Shift-Mod-Enter': insertNewline('linebreak'),
    Backspace: backspace,
    'Mod-Backspace': deleteWord(-1, backspace),
    Delete: del,
    'Mod-Delete': deleteWord(1, del),
    'Mod-z': undo,
    'Mod-y': redo,
    'Shift-Mod-z': redo,
    'Mod-a': selectInset,
    'Alt-Mod-a': selectAll,
    'Mod-e': fontCommands.emph,
    'Mod-b': fontCommands.bold,
    'Alt-Mod-b': fontCommands.bold,
    'Mod-u': fontCommands.underline,
    'Shift-Mod-o': fontCommands.strikeout,
    'Shift-Mod-n': fontCommands.noun,
    'Alt-Mod-d': fontDefault,
    'Mod-\\': fontDefault,   // Google Docs' "Clear formatting"
    'Mod-m': (_s, _d, view) => (view ? insertMath(false)(view) : false),
    'Shift-Mod-m': (_s, _d, view) => (view ? insertMath(true)(view) : false),
    'Alt-Mod-n': (_s, _d, view) => (view ? insertMath(true, 'equation')(view) : false),
    'Mod-l': insertERT,
    'Alt-Mod-f': insertFootnote,
    'Alt-Mod-m': insertMarginal,
    'Alt-Mod-c': insertComment,
    'Alt-Shift-Mod-n': insertNote('Note'),
    'Mod-i': fontCommands.italic,          // the usual italic key; LyX's Ctrl+I (inset-toggle) moved to Ctrl+Alt+I
    'Alt-Mod-i': toggleInset,
    'Shift-Alt-Mod-i': ui(a => a.openDialog('inset')),
    'Mod- ': insertSpace('~'),
    'Alt-Mod- ': insertSpace('\\space{}'),
    'Shift-Mod- ': insertSpace('\\thinspace{}'),
    'Mod-.': insertSpecial('endofsentence'),
    'Alt-.': insertSpecial('ldots'),
    // Alt+- types an em dash (the hyphenation point it used to insert is invisible, so the key seemed
    // dead — and on a Mac it took over ⌥-, whose en dash reaches ProseMirror as Alt+- by key code)
    'Alt--': insertDash('em'),
    'Alt-Mod--': insertSpecial('nobreakdash'),
    'Shift-Mod-l': insertSpecial('ligaturebreak'),
    'Mod-/': insertSpecial('breakableslash'),
    'Alt-Shift-ArrowRight': changeDepth(1),
    'Alt-Shift-ArrowLeft': changeDepth(-1),
    // LyX M-Up "outline-up; paragraph-move-up": a list item moves with its sub-items (listmove.ts), else the paragraph
    'Alt-ArrowUp': chainCommands(moveListItem(-1), moveParagraph(-1)),
    'Alt-ArrowDown': chainCommands(moveListItem(1), moveParagraph(1)),
    // LyX site.bind Tab: completion-accept (the ghost suggestion, handled in ai/complete.ts);
    // cell-forward; depth-increment. Outside tables and lists the browser keeps the key.
    Tab: chainCommands(goToNextCell(1), listIndent(1)),
    'Shift-Tab': chainCommands(goToNextCell(-1), listIndent(-1)),
    // ⌘K / Ctrl+K: a link over the selection, as in Google Docs (editor/links.ts; LyX's Ctrl+K deleted to the
    // end of the paragraph). ⌘J / Ctrl+J: rewrite with AI, when that is switched on (Tools ▸ AI)
    'Mod-k': (_state, _dispatch, view) => { if (view) openLinkBox(view); return true; },
    'Mod-j': (_state, _dispatch, view) => !!view && rewriteEnabled() && openRewrite(view),
    'Mod-s': ui(a => a.save()),
    F2: ui(a => a.save()),
    'Mod-r': ui(a => a.viewPdf()),
    'Alt-Mod-j': ui(a => a.syncToPdf?.()),
    // LyX binds Ctrl+Shift+R to "update PDF" as well, but in a browser that is the hard-reload key:
    // hijacking it silently started a full latexmk build. Left to the browser on purpose.
    'Mod-f': ui(a => a.find()),
    F3: ui(a => a.find()),
    'Shift-Mod-e': ui(a => a.toggleTrackChanges()),
    'Alt-Mod-o': ui(a => a.toggleOutline()),
    'Alt-Mod-s': ui(a => a.toggleSource?.()),
    'Mod-=': ui(a => a.zoom(1)),
    'Mod-+': ui(a => a.zoom(1)),
    'Mod--': ui(a => a.zoom(-1)),
    'Mod-Alt-=': ui(a => a.textWidth?.(1)),
    'Mod-Alt-+': ui(a => a.textWidth?.(1)),
    'Mod-Alt--': ui(a => a.textWidth?.(-1)),
    'Mod-o': ui(a => a.openFile()),
    'Mod-n': ui(a => a.newFile()),
    'Shift-Mod-c': ui(a => a.openDialog('cite')),
    'Shift-Mod-g': ui(a => a.openDialog('graphics')),
    'Alt-Mod-t': ui(a => a.openDialog('table')),
    'Alt-Mod-p': ui(a => a.openDialog('paragraph')),
    'Shift-Mod-i': ui(a => a.openDialog('ref')),
    'Alt-Mod-l': ui(a => a.openDialog('label')),
    'Alt-Mod-k': (_state, _dispatch, view) => { if (view) openLinkBox(view); return true; },
    'Mod-ArrowRight': (state, dispatch, view) => arrowIntoMath(1)(state, dispatch, view) || false,
    ArrowRight: (state, dispatch, view) => arrowIntoMath(1)(state, dispatch, view),
    ArrowLeft: (state, dispatch, view) => arrowIntoMath(-1)(state, dispatch, view),
    // LyX: extending the selection over a formula/inset takes it whole, one chunk per keypress
    // (the browser's own shift-arrow can strand the head beside the widget)
    'Shift-ArrowRight': shiftOverAtom(1),
    'Shift-ArrowLeft': shiftOverAtom(-1),
    '"': smartQuote,
    '$': typeDollar,   // $…$ / $$…$$ typed as in LaTeX open an inline / display formula
    'Alt-"': insertQuote('l', 'e', 's'),
    'Shift-Mod-"': (state, dispatch) => dispatch ? (dispatch(state.tr.insertText('"')), true) : true,
    'Alt-m': (_s, _d, view) => (view ? insertMath(false)(view) : false),
    Escape: escapeInset,
  };
  // Google-Docs-style heading keys, with LyX's digits (the Alt+P chord): Ctrl/⌘+0…6 = Part, Chapter,
  // Section, Subsection, Subsubsection, Paragraph, Subparagraph; Ctrl/⌘+Alt+digit = the unnumbered
  // (*) variant. (Ctrl+0 used to reset the zoom — View ▸ Reset zoom, rebindable in the palette.)
  for (const [digit, layout] of Object.entries(LAYOUT_PREFIX)) {
    if (!/^[0-6]$/.test(digit)) continue;
    bindings[`Mod-${digit}`] = setKnownLayout(layout);
    bindings[`Alt-Mod-${digit}`] = setKnownLayout(layout + '*');
  }
  // Alt+Shift+- is the en dash; on a Mac ⌥⇧- stays macOS's own em dash (typed by the system)
  if (!isMac()) bindings['Shift-Alt--'] = insertDash('en');
  if (isMac()) {
    // ⌘M minimises the window, ⌘⌥C opens DevTools and ⌃R reloads — macOS/Chrome act on those
    // before or besides the page, so the Ctrl variants work as well (and the tour shows them)
    bindings['Ctrl-m'] = bindings['Mod-m'];
    bindings['Ctrl-Alt-c'] = bindings['Alt-Mod-c'];
    bindings['Ctrl-r'] = bindings['Mod-r'];
  }
  for (const k of Object.keys(bindings)) if (TRACED_KEY.test(k)) bindings[k] = traced(k, bindings[k]);
  return keymap(bindings);
}

/** LyX: Shift+arrow beside an atom (formula, graphic …) selects it whole as one chunk. */
const shiftOverAtom = (dir: -1 | 1): Command => (state, dispatch) => {
  const sel = state.selection;
  if (!(sel instanceof TextSelection)) return false;
  const $h = sel.$head;
  const node = dir > 0 ? $h.nodeAfter : $h.nodeBefore;
  if (!node || !ATOMS.has(node.type.name)) return false;
  dispatch?.(state.tr.setSelection(TextSelection.between(state.doc.resolve(sel.anchor), state.doc.resolve($h.pos + dir * node.nodeSize))).scrollIntoView());
  return true;
};

/** Escape: move the cursor out of the innermost inset (to its right). */
const escapeInset: Command = (state, dispatch) => {
  const $from = state.selection.$from;
  for (let d = $from.depth; d > 0; d--) {
    const n = $from.node(d);
    if (n.type.name === 'inset' || n.type.name === 'table') {
      const after = $from.after(d);
      dispatch?.(state.tr.setSelection(TextSelection.near(state.doc.resolve(after))));
      return true;
    }
  }
  return false;
};

export function isMac(): boolean { return /Mac|iPhone|iPad/.test(navigator.platform); }

export { toggleMathDisplay, type EditorState, type EditorView, insertHyphens };
